import { ref } from "vue";
import { get } from "lodash-es";
import { useApi } from "./api.mjs";
import { useEmitter } from "#nk/composables/emitter";
import { useState, useCookie, useSeoMeta } from "#imports";
const defaultPage = () => {
  return {
    title: null,
    description: null,
    canonical: null,
    context: [],
    theme: "default",
    layout: "default",
    heroImages: [],
    content: [],
    apiCalls: [],
    apiCallsContent: {},
    breadcrumbs: [],
    isRedirect: false,
    redirectTo: null,
    hasError: false,
    pagination: {
      page: 1,
      pages: 1
    },
    isLoading: false,
    isQueryOnlyChanging: false,
    url: null,
    query: null,
    name: null,
    hashCode: null
  };
};
export const isPageLoading = ref(false);
export const useCMS = () => {
  const page = useState("page", defaultPage);
  return page;
};
export function useSEO(meta = {
  title: null,
  description: null
}) {
  const page = useCMS();
  const seo = useSeoMeta({
    title: meta.title || page.value.title,
    description: meta.description || page.value.description
  });
  return seo;
}
export function updateBreadcrumb(Breadcrumb = {
  index: void 0,
  title: null,
  url: null
}) {
  const page = useCMS();
  const getBreadcrumb = page.value.breadcrumbs[Breadcrumb.index !== void 0 ? Breadcrumb.index : page.value.breadcrumbs.length - 1];
  if (getBreadcrumb) {
    getBreadcrumb.title = Breadcrumb.title || getBreadcrumb.title;
    getBreadcrumb.url = Breadcrumb.url || getBreadcrumb.url;
  }
}
export async function useCMSContent(to) {
  const page = useCMS();
  const emitter = useEmitter();
  const api = useApi();
  const route = to;
  const authorizationCookie = useCookie("authorization");
  const cookies = { authorizationCookie };
  const path = route.path;
  const cmsParams = "url=" + encodeURIComponent(path);
  const { query } = route;
  if (page.value.url === path && JSON.stringify(page.value.query) === JSON.stringify(query)) {
    return page;
  }
  if (path === "/" || path.startsWith("/_nuxt/"))
    return;
  if (route.query && route.path === page.value.url) {
    page.value.isQueryOnlyChanging = true;
  }
  page.value.isLoading = true;
  try {
    const { data } = await api.get("/api/cms/route?" + cmsParams);
    let { title, description, pageContent, canonical } = data;
    const {
      url,
      name,
      pageLayout,
      pageTheme,
      apiCalls,
      heroImages,
      breadcrumbs
    } = data;
    let pagination = {
      page: 1,
      pages: 1
    };
    const apiContent = {};
    if (apiCalls) {
      const mappedApiCalls = apiCalls.map(
        (apiCall) => {
          if (apiCall.options && apiCall.options.query) {
            Object.keys(apiCall.options.query).forEach((queryKey) => {
              if (apiCall.options.query[queryKey] === true && query[queryKey]) {
                if (queryKey === "rubrik_id" && typeof apiCall.options.query.rubriken === "undefined") {
                  apiCall.params.rubriken = query[queryKey];
                }
                apiCall.params[queryKey] = query[queryKey];
              }
            });
          }
          return apiCall;
        }
      );
      await Promise.all(
        mappedApiCalls.map(async (request) => {
          try {
            let headers = {};
            if (cookies.authorizationCookie.value && request.options?.useAuth) {
              headers = {
                authorization: cookies.authorizationCookie.value.token
              };
            }
            const { data: response } = await api.get(request.endpoint, {
              params: request.params,
              headers,
              validateStatus: (status) => status >= 200 && status < 500
            });
            apiContent[request.name] = response;
          } catch (error) {
            apiContent[request.name] = null;
          }
          return request;
        })
      );
      mappedApiCalls.forEach((apiCall) => {
        const apiCallContent = apiContent[apiCall.name];
        if (apiCallContent && apiCall.options?.useMeta) {
          let pageTitle = "";
          if (Object.prototype.hasOwnProperty.call(apiCallContent, "seo") && apiCallContent.seo.title != null && apiCallContent.seo.title.length > 0) {
            pageTitle = apiCallContent.seo.title;
          } else if (Object.prototype.hasOwnProperty.call(apiCallContent, "vorname") && Object.prototype.hasOwnProperty.call(apiCallContent, "nachname")) {
            pageTitle = `${apiCallContent.vorname} ${apiCallContent.nachname}`;
          } else {
            pageTitle = apiCallContent.titel;
          }
          title = pageTitle;
          let breadcrumbTitle = "";
          if (apiCallContent.vorname != null && apiCallContent.nachname != null) {
            breadcrumbTitle = `${apiCallContent.vorname} ${apiCallContent.nachname}`;
          }
          if (breadcrumbTitle.length === 0 && apiCallContent.titel != null) {
            breadcrumbTitle = apiCallContent.titel;
          }
          if (breadcrumbs?.length) {
            breadcrumbs[breadcrumbs.length - 1 >= 0 ? breadcrumbs.length - 1 : 0].title = breadcrumbTitle;
          }
          let pageDescription = "";
          if (Object.prototype.hasOwnProperty.call(apiCallContent, "seo") && apiCallContent.seo.description !== "") {
            pageDescription = apiCallContent.seo.description;
          } else {
            pageDescription = data.description;
          }
          description = pageDescription;
        }
        if (apiCall.options?.canonical) {
          try {
            const urlArray = route.url.split("/");
            const urlArraySynth = apiCall.options.canonical.split("/");
            const newUrlArray = urlArray.map(
              (part, index) => {
                let newPart = urlArraySynth[index];
                if (urlArraySynth[index].startsWith(":")) {
                  newPart = part;
                }
                return newPart;
              }
            );
            canonical = newUrlArray.join("/");
          } catch (error) {
            canonical = route.fullPath;
          }
        }
        if (apiCall.options && apiCall.options.query?.seite) {
          pagination = {
            page: query?.seite ? parseInt(query.seite) : 1,
            pages: apiCallContent?.seiten ? parseInt(apiCallContent.seiten) : 1
          };
        }
      });
      if (pageContent) {
        pageContent = pageContent.map((templateDefinition) => {
          if (templateDefinition.context) {
            Object.keys(templateDefinition.context).forEach((contextKey) => {
              if (contextKey === "$eval") {
                templateDefinition.context = get(
                  apiContent,
                  templateDefinition.context[contextKey]
                );
              } else if (typeof templateDefinition.context[contextKey] === "object" && typeof templateDefinition.context[contextKey].$eval !== "undefined") {
                templateDefinition.context[contextKey] = get(
                  apiContent,
                  templateDefinition.context[contextKey].$eval
                );
              }
            });
          }
          if (typeof templateDefinition.options === "undefined") {
            templateDefinition.options = {};
          }
          return templateDefinition;
        });
      }
      page.value.isRedirect = false;
      page.value.redirectTo = null;
      if (mappedApiCalls.length) {
        mappedApiCalls.forEach((apiCall) => {
          const apiCallContent = apiContent[apiCall.name];
          if (apiCall.options?.redirectOnSingleEntry != null && apiCallContent.data && Array.isArray(apiCallContent.data) && apiCallContent.data.length === 1 && query.seite == null) {
            const [singleEntry] = apiCallContent.data;
            page.value.isRedirect = true;
            page.value.redirectTo = singleEntry.detailURL;
          }
        });
      }
    }
    page.value.title = title;
    page.value.description = description;
    page.value.canonical = canonical;
    page.value.layout = pageLayout;
    page.value.theme = pageTheme;
    page.value.context = pageContent;
    page.value.apiCalls = apiCalls;
    page.value.apiCallsContent = apiContent;
    page.value.breadcrumbs = breadcrumbs;
    page.value.url = url;
    page.value.heroImages = heroImages;
    page.value.name = name;
    page.value.pagination = pagination;
    page.value.query = query;
    page.value.hasError = false;
    return page;
  } catch (error) {
    page.value.url = path;
    page.value.query = query;
    page.value.theme = "default";
    page.value.layout = "default";
    page.value.context = [];
    page.value.apiCalls = [];
    page.value.apiCallsContent = {};
    page.value.breadcrumbs = [];
    page.value.hasError = true;
    page.value.pagination = {
      page: 1,
      pages: 1
    };
    page.value.title = "Seite nicht gefunden";
    page.value.description = "Die angeforderte Seite konnte nicht gefunden werden.";
    return page;
  } finally {
    page.value.isLoading = false;
    page.value.isQueryOnlyChanging = false;
    emitter.emit("nk:cms-finished");
  }
}
